import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, MapPin, Clock, Send, CheckCircle, Building2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL = "techieroshan@gmail.com";

const subjects = [
  "Schedule a Demo",
  "Partnership / B2B Enquiry",
  "Club or League Setup",
  "Press & Media",
  "Technical Support",
  "Billing Question",
  "Privacy / Legal",
  "General Enquiry",
];

const reasons = [
  {
    icon: Building2,
    title: "Corporate Wellness",
    body: "Set up a company sports league and get your team active. We'll walk you through the setup.",
  },
  {
    icon: Mail,
    title: "Club Partnerships",
    body: "Bring ActivitySports to your tennis club, recreation center, or sports facility.",
  },
  {
    icon: ArrowRight,
    title: "Media & Press",
    body: "Cover the future of individual sports matching. Request a press kit or arrange an interview.",
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: subjects[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Hero */}
      <div className="bg-brand-navy text-white py-14 px-4 dark-section">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 text-brand-green text-sm font-bold uppercase tracking-widest mb-3">
            <Mail size={14} /> Contact
          </div>
          <h1 className="text-4xl md:text-5xl font-hero font-black mb-3">Get in Touch</h1>
          <p className="text-white/70 max-w-xl">
            Whether you're curious about a demo, want to set up a corporate league, or have a
            general question — we'd love to hear from you.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-14">
        <div className="grid lg:grid-cols-5 gap-12">

          {/* Left: form */}
          <div className="lg:col-span-3">
            {success ? (
              <div className="bg-white border border-brand-green/30 rounded-2xl p-10 flex flex-col items-center text-center shadow-sm" data-testid="contact-success">
                <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mb-5">
                  <CheckCircle className="text-brand-green" size={32} />
                </div>
                <h2 className="text-2xl font-heading font-bold text-brand-navy mb-2">Message sent!</h2>
                <p className="text-gray-500 text-sm mb-6">
                  Thanks for reaching out. We typically reply within 2 business days.
                  You'll hear from us at <strong>{form.email}</strong>.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setSuccess(false); setForm({ name: "", email: "", subject: subjects[0], message: "" }); }}
                    className="px-5 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:border-brand-navy hover:text-brand-navy transition-colors"
                  >
                    Send another
                  </button>
                  <Link to="/" className="px-5 py-2 bg-brand-orange text-white rounded-xl text-sm font-bold hover:bg-brand-orange-hover transition-colors">
                    Back to home
                  </Link>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm space-y-5"
                data-testid="contact-form"
              >
                <h2 className="text-lg font-heading font-bold text-brand-navy">Send us a message</h2>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600" data-testid="contact-error">
                    {error}
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="Full-Name" className="block text-sm font-medium text-brand-navy mb-1.5">Full name <span aria-hidden="true" className="text-brand-orange">*</span></label><input id="Full-Name"
                      type="text"
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Alex Johnson"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-brand-navy placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="Email-Address" className="block text-sm font-medium text-brand-navy mb-1.5">Email address <span aria-hidden="true" className="text-brand-orange">*</span></label><input id="Email-Address"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.com"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-brand-navy placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-colors"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-navy mb-1.5">Subject</label>
                  <select
                    aria-label="Message subject"
                    value={form.subject}
                    onChange={set("subject")}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-brand-navy text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-colors"
                    data-testid="contact-subject-select"
                  >
                    {subjects.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label htmlFor="Message" className="block text-sm font-medium text-brand-navy mb-1.5">Message <span aria-hidden="true" className="text-brand-orange">*</span></label><textarea id="Message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us more about what you need..."
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-brand-navy placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green transition-colors resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange-hover transition-colors disabled:opacity-50 shadow-sm"
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  {loading ? "Sending…" : "Send Message"}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Or email directly: <a href={`mailto:${EMAIL}`} className="text-brand-green hover:underline">{EMAIL}</a>
                </p>
              </form>
            )}
          </div>

          {/* Right: contact info + reasons */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact info card */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-brand-navy mb-4 text-sm">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-brand-green/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail size={14} className="text-brand-green" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Email</p>
                    <a href={`mailto:${EMAIL}`} className="text-sm text-brand-navy font-medium hover:text-brand-green transition-colors">{EMAIL}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-brand-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={14} className="text-brand-orange" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Headquarters</p>
                    <p className="text-sm text-brand-navy font-medium">Kosansh Solutions Inc</p>
                    <p className="text-xs text-gray-500">Fort Worth, TX, United States</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-brand-navy/5 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock size={14} className="text-brand-navy" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Response time</p>
                    <p className="text-sm text-brand-navy font-medium">Within 2 business days</p>
                    <p className="text-xs text-gray-500">Mon–Fri, Central Time</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reasons to contact */}
            <div className="space-y-3">
              {reasons.map(({ icon: Icon, title, body }) => (
                <div key={title} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex gap-3">
                  <div className="w-8 h-8 bg-brand-navy/5 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={15} className="text-brand-navy" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-brand-navy mb-0.5">{title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick links */}
            <div className="bg-brand-navy/5 border border-brand-navy/10 rounded-xl p-4">
              <p className="text-xs font-bold text-brand-navy uppercase tracking-widest mb-3">Quick Links</p>
              <div className="space-y-2">
                {[
                  { label: "Privacy Policy", to: "/privacy" },
                  { label: "Terms of Service", to: "/terms" },
                  { label: "Safety Center", to: "/safety" },
                  { label: "Community Guidelines", to: "/community" },
                ].map(({ label, to }) => (
                  <Link key={to} to={to} className="flex items-center justify-between text-sm text-gray-600 hover:text-brand-green transition-colors">
                    {label} <ArrowRight size={12} />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
